import { useMemo } from 'react';

import { useBulkDogpileStatus } from '@/sections/customerManagement/hooks/data/useBulkDogpileStatus';
import { useCustomerManagement } from '@/sections/customerManagement/hooks/useCustomerManagement';
import {
  DogPileCustomer,
  DogPileTierCounts,
  UrgencyTier,
} from '@/sections/customerManagement/types/dogPile.types';
import {
  calculatePriorityScore,
  calculateTimeRemaining,
  categorizeCustomerType,
  getUrgencyMultiplier,
  getUrgencyTierFromScore,
} from '@/sections/customerManagement/utils/priorityScoring';

export interface UseDogPilePrioritizationReturn {
  subscriptions: DogPileCustomer[];
  projects: DogPileCustomer[];
  freemium: DogPileCustomer[];
  subscriptionTierCounts: DogPileTierCounts;
  projectTierCounts: DogPileTierCounts;
  freemiumTierCounts: DogPileTierCounts;
  isLoading: boolean;
}

function buildTierCounts(customers: DogPileCustomer[]): DogPileTierCounts {
  const counts: DogPileTierCounts = {
    total: customers.length,
    critical: 0,
    high: 0,
    moderate: 0,
    healthy: 0,
  };

  for (const c of customers) {
    switch (c.urgencyTier) {
      case 'CRITICAL':
        counts.critical++;
        break;
      case 'HIGH':
        counts.high++;
        break;
      case 'MODERATE':
        counts.moderate++;
        break;
      case 'HEALTHY':
        counts.healthy++;
        break;
    }
  }

  return counts;
}

export function useDogPilePrioritization(): UseDogPilePrioritizationReturn {
  const { allCustomers, customersStatus } = useCustomerManagement();
  const dogpileStatusQuery = useBulkDogpileStatus();

  const { subscriptions, projects, freemium } = useMemo(() => {
    const subs: DogPileCustomer[] = [];
    const projs: DogPileCustomer[] = [];
    const free: DogPileCustomer[] = [];

    for (const customer of allCustomers) {
      // Skip completed customers
      if (customer.status === 'Completed') continue;

      const category = categorizeCustomerType(customer.type);
      if (!category) continue;

      const timeRemaining = calculateTimeRemaining(
        customer.subscriptionEndDate
      );
      const { multiplier, tier } = getUrgencyMultiplier(
        timeRemaining,
        category
      );
      const isExpired = timeRemaining?.isExpired ?? false;

      // Skip expired customers — dogpile should only show active customers
      if (isExpired) continue;

      // Skip customers marked for deletion
      if (customer.type === 'DELETE') continue;

      const priorityScore = calculatePriorityScore(
        customer.evm,
        multiplier,
        isExpired
      );
      const urgencyTier = getUrgencyTierFromScore(tier, isExpired);

      const dogpileStatus =
        dogpileStatusQuery.dataMap.get(customer.email) ?? 'none';

      const enriched: DogPileCustomer = {
        ...customer,
        rank: 0, // Assigned after sorting
        priorityScore,
        urgencyMultiplier: multiplier,
        urgencyTier,
        timeRemaining,
        dogpileStatus,
        category,
      };

      if (category === 'subscriptions') {
        subs.push(enriched);
      } else if (category === 'freemium') {
        free.push(enriched);
      } else {
        projs.push(enriched);
      }
    }

    // Urgency tier ordering: CRITICAL first, HEALTHY last
    const tierOrder: Record<UrgencyTier, number> = {
      CRITICAL: 0,
      HIGH: 1,
      MODERATE: 2,
      HEALTHY: 3,
    };

    // Sort: by urgency tier (most urgent first), then by priorityScore desc within same tier
    const sortFn = (a: DogPileCustomer, b: DogPileCustomer): number => {
      const tierDiff = tierOrder[a.urgencyTier] - tierOrder[b.urgencyTier];
      if (tierDiff !== 0) return tierDiff;
      return b.priorityScore - a.priorityScore;
    };

    subs.sort(sortFn);
    projs.sort(sortFn);
    free.sort(sortFn);

    // Assign ranks after sorting
    subs.forEach((c, i) => {
      c.rank = i + 1;
    });
    projs.forEach((c, i) => {
      c.rank = i + 1;
    });
    free.forEach((c, i) => {
      c.rank = i + 1;
    });

    return { subscriptions: subs, projects: projs, freemium: free };
  }, [allCustomers, dogpileStatusQuery.dataMap]);

  const subscriptionTierCounts = useMemo(
    () => buildTierCounts(subscriptions),
    [subscriptions]
  );

  const projectTierCounts = useMemo(
    () => buildTierCounts(projects),
    [projects]
  );

  const freemiumTierCounts = useMemo(
    () => buildTierCounts(freemium),
    [freemium]
  );

  return {
    subscriptions,
    projects,
    freemium,
    subscriptionTierCounts,
    projectTierCounts,
    freemiumTierCounts,
    isLoading:
      customersStatus === 'pending' || dogpileStatusQuery.status === 'pending',
  };
}
